import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AppLayout } from "@/components/AppLayout";
import { CommandPalette } from "@/components/CommandPalette";
import { KeyboardShortcutsHelp } from "@/components/KeyboardShortcutsHelp";
import { useServiceWorker } from "@/hooks/useServiceWorker";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { toast } from "sonner";

// Helper to handle dynamic import errors (stale chunks after deployment)
const lazyWithRetry = (importFn: () => Promise<any>) => {
  return lazy(() =>
    importFn().catch((error) => {
      // If it's a chunk loading error, reload the page to get fresh assets
      if (error.message.includes("Failed to fetch dynamically imported module")) {
        console.warn("Chunk loading failed, reloading page...", error);
        window.location.reload();
        // Return a never-resolving promise to prevent React from rendering anything
        return new Promise(() => {});
      }
      throw error;
    })
  );
};

// Lazy load pages for code splitting with retry logic
const Index = lazyWithRetry(() => import("./pages/Index"));
const Analysis = lazyWithRetry(() => import("./pages/Analysis"));
const SharedAnalysis = lazyWithRetry(() => import("./pages/SharedAnalysis"));
const SharedWithMe = lazyWithRetry(() => import("./pages/SharedWithMe"));
const GuidedWritingPage = lazyWithRetry(() => import("./pages/GuidedWritingPage"));
const Profile = lazyWithRetry(() => import("./pages/Profile"));
const Settings = lazyWithRetry(() => import("./pages/Settings"));
const AllAnalyses = lazyWithRetry(() => import("./pages/AllAnalyses"));
const AllChats = lazyWithRetry(() => import("./pages/AllChats"));
const Favorites = lazyWithRetry(() => import("./pages/Favorites"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const Demo = lazyWithRetry(() => import("./pages/Demo"));

const Flashcards = lazyWithRetry(() => import("./pages/Flashcards"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazyWithRetry(() => import("./pages/TermsOfService"));
const About = lazyWithRetry(() => import("./pages/About"));
const Upgrade = lazyWithRetry(() => import("./pages/Upgrade"));
const AdminCosts = lazyWithRetry(() => import("./pages/AdminCosts"));
const Library = lazyWithRetry(() => import("./pages/Library"));
const LibraryBook = lazyWithRetry(() => import("./pages/LibraryBook"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000, // 2 minutes
      gcTime: 10 * 60 * 1000,   // 10 minutes
    },
  },
});

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => {
  useServiceWorker();

  // Global handler for unhandled promise rejections (fallback safety net)
  useEffect(() => {
    const handleRejection = (event: PromiseRejectionEvent) => {
      const errorMessage = event.reason?.message || String(event.reason);
      
      // Handle dynamic import failures by reloading
      if (errorMessage.includes("Failed to fetch dynamically imported module")) {
        console.warn("Unhandled chunk loading error, reloading...", event.reason);
        event.preventDefault();
        window.location.reload();
        return;
      }
      
      // Log other unhandled rejections
      console.error("Unhandled rejection:", event.reason);
    };

    window.addEventListener("unhandledrejection", handleRejection);
    return () => window.removeEventListener("unhandledrejection", handleRejection);
  }, []);
  
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <SubscriptionProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <CommandPalette />
              <KeyboardShortcutsHelp />
              <AppLayout>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/analysis/:id" element={<Analysis />} />
                    <Route path="/shared/:token" element={<SharedAnalysis />} />
                    <Route path="/shared-with-me" element={<SharedWithMe />} />
                    <Route path="/guided-writing" element={<GuidedWritingPage />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/analyses" element={<AllAnalyses />} />
                    <Route path="/chats" element={<AllChats />} />
                    <Route path="/favorites" element={<Favorites />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/demo" element={<Demo />} />
                    
                    <Route path="/flashcards" element={<Flashcards />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/terms" element={<TermsOfService />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/upgrade" element={<Upgrade />} />
                    <Route path="/library" element={<Library />} />
                    <Route path="/library/:bookId" element={<LibraryBook />} />
                    <Route path="/admin/costs" element={<AdminCosts />} />
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </AppLayout>
            </BrowserRouter>
          </SubscriptionProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
};

export default App;
